import { useEffect, useState } from "react";
import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { useToast } from "@/src/components/ui/use-toast";
import { fetch } from "@/src/lib/fetch";

/**
 * Link a booking to its HubSpot deal after the fact (bookings whose deal was
 * never minted at the offer step). Search by client email, deal name or deal
 * id; the booking's email is searched first.
 */
type Hit = {
  id: string; name: string | null; stage: string | null; amount: number | null; currency: string | null; createdAt: string | null;
  helmDealId: string | null; contact: { name: string | null; email: string | null } | null; linkedBooking: { id: string; slug: string | null } | null;
};
const money = (v: number | null, c: string | null) => (v == null ? "" : new Intl.NumberFormat("en-EN", { style: "currency", currency: c || "EUR", maximumFractionDigits: 0 }).format(v));

export function LinkDealDialog({ bookingId, bookingLabel, initialQuery, onClose, onLinked }: { bookingId: string; bookingLabel?: string | null; initialQuery?: string | null; onClose: () => void; onLinked: (dealId: string) => void }) {
  const { toast } = useToast();
  const [q, setQ] = useState(initialQuery ?? "");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);

  const search = (term: string) => {
    if (term.trim().length < 2) { setHits(null); return; }
    setSearching(true);
    fetch<{ data: Hit[] }>(`/api/bookings/deal-link/search?q=${encodeURIComponent(term.trim())}`)
      .then((r: any) => setHits(r.data ?? []))
      .catch((e: any) => { setHits([]); toast({ title: "HubSpot search failed", description: e?.message, variant: "destructive" }); })
      .finally(() => setSearching(false));
  };
  useEffect(() => { if (initialQuery) search(initialQuery); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const link = (h: Hit, force = false) => {
    setLinking(h.id);
    fetch(`/api/bookings/${bookingId}/link-deal`, "POST", { dealId: h.id, force })
      .then(() => { toast({ title: "Deal linked", description: `${h.name ?? h.id} is now this booking's HubSpot deal.` }); onLinked(h.id); })
      .catch((e: any) => toast({ title: "Couldn't link", description: e?.message, variant: "destructive" }))
      .finally(() => setLinking(null));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Link HubSpot deal{bookingLabel ? ` · ${bookingLabel}` : ""}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">This booking has no HubSpot deal, so it can't be confirmed, invoiced or tracked by ops. Find the deal by the client's email, the deal name, or paste a deal id.</p>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); search(q); }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="client email, deal name, or deal id" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
          <Button type="submit" variant="outline" disabled={searching}>{searching ? "…" : "Search"}</Button>
        </form>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {hits && hits.length === 0 && <p className="text-sm text-slate-500">No deal found for "{q}" — the client may have no HubSpot contact yet.</p>}
          {(hits ?? []).map((h) => (
            <div key={h.id} className="flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="truncate font-medium">{h.name ?? h.id}</p>
                <p className="text-xs text-slate-500">
                  {[h.stage, money(h.amount, h.currency), h.contact?.name, h.contact?.email, h.helmDealId ? `#${h.helmDealId}` : null, h.createdAt ? new Date(h.createdAt).toLocaleDateString("en-GB") : null].filter(Boolean).join(" · ")}
                </p>
                {h.linkedBooking && <p className="text-xs text-amber-700">Already linked to booking {h.linkedBooking.slug ?? h.linkedBooking.id.slice(0, 8)}</p>}
              </div>
              <div className="flex shrink-0 gap-1">
                <a className="text-xs text-blue-600 hover:underline" href={`https://app-eu1.hubspot.com/contacts/143390230/record/0-3/${h.id}`} target="_blank" rel="noreferrer noopener">Open ↗</a>
                <Button size="sm" variant={h.linkedBooking ? "outline" : "default"} disabled={linking === h.id} onClick={() => (h.linkedBooking ? window.confirm("This deal is already on another booking. Link it here too?") && link(h, true) : link(h))}>
                  {linking === h.id ? "Linking…" : "Link"}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
